import { useCallback } from 'react';

import { noop } from '@tanstack/react-query';
import get from 'lodash-es/get';
import { CirclePlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Button } from '@/components/ui/button';
import { generateOption } from '@/redux/dialogs/helper';
import { addNewRequisiteOption, reorderRequisiteOption } from '@/redux/nodes/actions';
import { Requisite, RequisiteOptionType } from '@/shared/types/node';
import { MAX_TEXT_ACTION_OPTIONS } from '@/util/constants';

import RequisiteOption from './RequisiteOption/RequisiteOption';

export const QuickOptions = ({
  requisite,
  updateErrors,
}: {
  requisite: Requisite;
  updateErrors: (key: string, error: string | null) => void;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const handleDragRequisiteOption = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      dispatch(
        reorderRequisiteOption({ dragIndex, hoverIndex, requisiteId: requisite?.requisite_id })
      );
    },
    [dispatch, requisite?.requisite_id]
  );

  const addRequisiteOption = useCallback(() => {
    dispatch(
      addNewRequisiteOption({ requisiteId: requisite?.requisite_id, option: generateOption() })
    );
  }, [dispatch, requisite?.requisite_id]);
  const requisiteOptions: RequisiteOptionType[] = get(requisite, 'actions[0].options', []);

  const isAddQuickOptionDisabled =
    requisiteOptions.length === MAX_TEXT_ACTION_OPTIONS || !requisite?.actions;
  return (
    <>
      <div className="mb-6 w-full text-right">
        <Button
          disabled={isAddQuickOptionDisabled}
          size="sm"
          variant="ghost-neutral"
          onClick={addRequisiteOption}
          data-testid="add-quick-option"
        >
          <CirclePlusIcon
            color={isAddQuickOptionDisabled ? 'var(--muted-foreground)' : undefined}
            size={16}
          />
          {t('dialog.question.add_quick_option')}
        </Button>
      </div>

      {requisiteOptions.map((option, index) => (
        <RequisiteOption
          key={option.option_id}
          option={option}
          expanded={false}
          index={index}
          moveItem={handleDragRequisiteOption}
          dropItem={noop}
          requisiteId={requisite?.requisite_id}
          updateErrors={updateErrors}
        />
      ))}
    </>
  );
};
